import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Tag, Layers, Image, FileText, HelpCircle, Star, Inbox, ArrowRight, Mail, Phone, MapPin, Calendar, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const LEAD_TYPE_BADGE = {
  catalogue: "bg-navy/20 text-navy",
  quotation: "bg-gold/20 text-gold",
  contact: "bg-emerald_brand/20 text-emerald-500",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const cards = [
    { label: "New Leads", value: stats?.leads_new ?? "—", total: stats?.leads_total ?? 0, icon: Inbox, to: "/admin/leads", accent: true },
    { label: "Products", value: stats?.products ?? "—", icon: Package, to: "/admin/products" },
    { label: "Brands", value: stats?.brands ?? "—", icon: Tag, to: "/admin/brands" },
    { label: "Categories", value: stats?.categories ?? "—", icon: Layers, to: "/admin/categories" },
    { label: "Gallery", value: stats?.gallery ?? "—", icon: Image, to: "/admin/gallery" },
    { label: "Blogs", value: stats?.blogs ?? "—", icon: FileText, to: "/admin/blogs" },
    { label: "FAQs", value: stats?.faqs ?? "—", icon: HelpCircle, to: "/admin/faqs" },
    { label: "Testimonials", value: stats?.testimonials ?? "—", icon: Star, to: "/admin/testimonials" },
  ];

  const leadBreakdown = [
    { l: "Catalogue Requests", v: stats?.leads_catalogue ?? 0 },
    { l: "Quotation Requests", v: stats?.leads_quotation ?? 0 },
    { l: "Contact Enquiries", v: stats?.leads_contact ?? 0 },
  ];

  return (
    <div data-testid={ADMIN.dashboard} className="space-y-8">
      <div>
        <p className="section-label mb-2">Overview</p>
        <h1 className="serif-display text-4xl text-foreground">Business Dashboard</h1>
        <p className="mt-2 text-sm text-foreground/70">Real-time snapshot of catalogues, brands, and business enquiries.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.04 }}
          >
            <Link
              to={c.to}
              className={`block p-5 rounded-md border transition-all hover:-translate-y-0.5 ${
                c.accent ? "border-gold/60 bg-gold/5 hover:border-gold" : "border-border/60 bg-card hover:border-gold/60"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className={`h-9 w-9 rounded-sm flex items-center justify-center ${c.accent ? "bg-gold/20 text-gold" : "bg-secondary text-foreground/70"}`}>
                  <c.icon size={16} strokeWidth={1.5} />
                </div>
                <ArrowRight size={14} className="text-foreground/40" />
              </div>
              <p className="mt-4 font-serif text-3xl text-foreground">{c.value}</p>
              <p className="text-[0.65rem] tracking-widest uppercase text-foreground/60 mt-1">{c.label}</p>
              {c.total !== undefined && (
                <p className="mt-2 text-[0.65rem] text-foreground/50">of {c.total} total</p>
              )}
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-md border border-border/60 bg-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="section-label text-[0.65rem]">Recent Business Enquiries</p>
              <h3 className="mt-1 font-serif text-xl">Latest Leads</h3>
            </div>
            <Link to="/admin/leads" className="text-xs tracking-widest uppercase text-gold hover:underline flex items-center gap-1">
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {(!stats?.recent_leads || stats.recent_leads.length === 0) ? (
            <div className="py-10 text-center text-sm text-foreground/50">No enquiries yet.</div>
          ) : (
            <ul className="divide-y divide-border/60">
              {stats.recent_leads.map((l) => (
                <li key={l.id} className="py-3 flex items-center gap-4">
                  <div className={`h-8 w-8 rounded-sm flex items-center justify-center shrink-0 text-xs font-semibold uppercase ${
                    LEAD_TYPE_BADGE[l.type] || "bg-secondary text-foreground/70"
                  }`}>{l.type?.[0] || "L"}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{l.contact_person || "Unnamed"} <span className="text-foreground/50">— {l.company_name || l.city || "—"}</span></p>
                    <div className="flex items-center gap-3 text-[0.7rem] text-foreground/60 mt-0.5">
                      <span className="flex items-center gap-1"><Phone size={10} /> {l.phone}</span>
                      {l.email && <span className="flex items-center gap-1 hidden md:flex"><Mail size={10} /> {l.email}</span>}
                    </div>
                  </div>
                  <span className="text-[0.65rem] tracking-widest uppercase text-foreground/50 flex items-center gap-1 shrink-0">
                    <Calendar size={10} /> {new Date(l.created_at).toLocaleDateString("en-IN")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-6 rounded-md border border-border/60 bg-card">
          <p className="section-label text-[0.65rem]">Lead Breakdown</p>
          <h3 className="mt-1 font-serif text-xl">By Type</h3>
          <div className="mt-5 space-y-4">
            {leadBreakdown.map((b) => (
              <div key={b.l}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-foreground/80">{b.l}</span>
                  <span className="text-foreground">{b.v}</span>
                </div>
                <div className="h-1.5 bg-secondary rounded-sm overflow-hidden">
                  <div
                    className="h-full bg-gold"
                    style={{ width: `${stats?.leads_total ? (b.v / stats.leads_total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-border/60 flex items-center gap-3 text-sm">
            <TrendingUp size={16} className="text-gold" />
            <span className="text-foreground/70">Live from CMS database</span>
          </div>
        </div>
      </div>

      <div className="p-6 rounded-md border border-border/60 bg-card">
        <p className="section-label text-[0.65rem]">Quick Actions</p>
        <h3 className="mt-1 font-serif text-xl mb-4">Frequently used</h3>
        <div className="flex flex-wrap gap-2">
          {[
            { to: "/admin/products", label: "Add Product" },
            { to: "/admin/brands", label: "Add Brand" },
            { to: "/admin/blogs", label: "Publish Blog" },
            { to: "/admin/gallery", label: "Upload Media" },
            { to: "/admin/settings", label: "Site Settings" },
            { to: "/admin/account", label: "Change Password" },
          ].map((a) => (
            <Link key={a.to} to={a.to} className="text-xs tracking-widest uppercase text-foreground/70 border border-border/60 hover:border-gold hover:text-gold rounded-sm px-4 py-2 transition-colors">
              {a.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
